import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const ContextualBreadcrumb = () => {
  const location = useLocation();
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const pathSegments = location?.pathname?.split('/')?.filter(Boolean);

  const breadcrumbMap = {
    'homepage': { label: 'Projects', icon: 'LayoutDashboard' },
    'project-creation': { label: 'New Project', icon: 'Plus' },
    'project-details': { label: 'Project Details', icon: 'FolderOpen' },
    'phase-management': { label: 'Phase Management', icon: 'ListChecks' },
    'inbox': { label: 'Inbox', icon: 'Inbox' },
    'login': { label: 'Login', icon: 'LogIn' }
  };

  const generateBreadcrumbs = () => {
    const breadcrumbs = [
      { label: 'Projects', path: '/homepage', icon: 'LayoutDashboard' }
    ];

    pathSegments?.forEach((segment, index) => {
      const path = `/${pathSegments?.slice(0, index + 1)?.join('/')}`;
      const breadcrumbInfo = breadcrumbMap?.[segment];
      
      if (breadcrumbInfo && segment !== 'homepage') {
        breadcrumbs?.push({
          label: breadcrumbInfo?.label,
          path: path,
          icon: breadcrumbInfo?.icon
        });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs?.length <= 1 || location?.pathname === '/login') {
    return null;
  }

  if (isMobile) {
    const currentPage = breadcrumbs?.[breadcrumbs?.length - 1];
    const previousPage = breadcrumbs?.[breadcrumbs?.length - 2];

    return (
      <nav className="flex items-center gap-2 px-4 py-3 bg-background border-b border-border" aria-label="Breadcrumb">
        <Link
          to={previousPage?.path}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth min-h-[44px] px-2 py-1 -ml-2 rounded-lg hover:bg-muted"
          aria-label="Go back"
        >
          <Icon name="ChevronLeft" size={20} strokeWidth={2} />
          <span className="text-sm font-medium">Back</span>
        </Link>
        <div className="flex items-center gap-2 text-foreground">
          <Icon name={currentPage?.icon} size={18} strokeWidth={2} />
          <span className="text-sm font-semibold">{currentPage?.label}</span>
        </div>
      </nav>
    );
  }

  return (
    <nav className="flex items-center gap-2 px-4 md:px-6 lg:px-8 py-3 bg-background border-b border-border" aria-label="Breadcrumb">
      <ol className="flex items-center gap-2 flex-wrap">
        {breadcrumbs?.map((crumb, index) => {
          const isLast = index === breadcrumbs?.length - 1;
          const isFirst = index === 0;

          return (
            <li key={crumb?.path} className="flex items-center gap-2">
              {!isFirst && (
                <Icon 
                  name="ChevronRight" 
                  size={16} 
                  className="text-muted-foreground" 
                  strokeWidth={2}
                />
              )}
              {isLast ? (
                <div className="flex items-center gap-2 text-foreground">
                  <Icon name={crumb?.icon} size={18} strokeWidth={2} />
                  <span className="text-sm font-semibold">{crumb?.label}</span>
                </div>
              ) : (
                <Link
                  to={crumb?.path}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth min-h-[32px] px-2 py-1 rounded-lg hover:bg-muted"
                >
                  <Icon name={crumb?.icon} size={18} strokeWidth={2} />
                  <span className="text-sm font-medium">{crumb?.label}</span>
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default ContextualBreadcrumb;